import { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import amazonLogo from '../../images/amazon_icon.png';
import unknownImage from '../../images/unknown.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEdit,
  faTrashCan
} from '@fortawesome/free-solid-svg-icons';
import {
  faMessage,
  faThumbsUp,
} from '@fortawesome/free-regular-svg-icons';
import ConfirmButton from '../ConfirmButton';
import UserContext from '../../hooks/UserContext';
import Comments from '../Comments';
import ListContext from '../../hooks/ListContext';
import Modal from '../Modal';
import { NO_OP } from '../../constants';

const commonCss = css`
position: relative;
flex-grow: 1;
display: flex;
flex-direction: column;
border-radius: 5px;
border: 1px solid  #bbbbbb;

@media print {
  break-inside: avoid;
}

&.is-reserved {
  background-color: gray;
}

.logo {
  > img {
    position: absolute;
    top: 5px;
    left: 5px;
    background-color: rgba(255,255,255,0.8);
    width: 40px;
    height: 40px;
    padding: 5px;
    border-radius: 50%;
    z-index: 10;
  }
}

.controls {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    color: #7777ff;
    border-top: 1px solid #dddddd;
    position: relative;
    padding: 10px;
    gap: 15px;
    align-items: center;

    & .buttons {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 10px;
      font-size: 0.75em;
      flex-grow: 1;

      > .buy {
        background-color: #dfc21c;
        color: black;
        border-color: #dfc21c;
      }

      > .release {
        background-color: green;
        color: white;
        border-color: green;
      }
    }

    & .comments {
      display: flex;
      gap: 5px;
      align-items: center; 
      > .icon {
        font-size: 1.25em;
        margin-top: 2px;
      }
    }
    & .likes {
      display: flex;
      gap: 5px;
      align-items: center;

      & .hover-likes {
        top: -2.25em;
        left: 0em;
        display: flex;
        gap: 3px;
        position: absolute;
        flex-wrap: wrap;
        z-index: 100;
        align-items: center;
        & .emoji {
          font-size: 1.25em;
          background-color: white;
          border: 1px solid gray;
          border-radius: 3px;
          padding: 10px;
          &.selected {
            background-color: #ccccff;
          }
          &:hover {
            background-color: blue;
          }
        }
      }

      & .show-likes {
        display: flex;
        gap: 5px;
        align-items: center;
        font-size: 0.75em;
      }
    }
  }

.data {
  display: grid;
  grid-template-columns: 100px auto;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  font-size: 1em;

  > .image {
      display: flex;
      justify-content: center;
      overflow: hidden;
    > img {
      height: 100px;
    }
  }
  .details {
    padding: 5px;
    display: flex;
    gap: 5px;
    flex-direction: column;
    flex-grow: 1;
    height: 100%;

    .by-line {
      font-size: 0.75em;
      font-weight: bold;
    }

    .itemTitle {
      font-weight: bold;
      &.link {
        color: blue;
        &:hover {
          cursor: pointer;
        }
       }
     }
    }
    
    .price {
      font-weight: bold;
      padding-top: 5px;
    }

    .addedOn {
      font-size: 0.75em;
    }
  }
`;
const CommonItemInfo = styled(({ className, item, toggleLike, children }) => {
  const { user } = useContext(UserContext);
  const {
    isOwner,
    canDeleteItem,
    canEditItem,
    onDeleteItem,
    onEditItem,
    onClaimItem,
    onReleaseItem,
    members,
    context
  } = useContext(ListContext);

  const [showEmojiList, setShowEmojiList] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(item.commentCount ?? 0);
  const [showConfirmReserve, setShowConfirmReserve] = useState(false);

  const emojiList = [
    '\u{1F44D}', '\u{1F497}', '\u{1F44B}', '\u{1F44A}',
    '\u{261D}', '\u{1F602}', '\u{1F603}', '\u{1F431}', '\u{1F436}', 'X'];
  const SHOW_COUNT = 3;
  const showLikes = item.likes?.slice(0, SHOW_COUNT - 1);
  const moreCount = showLikes ? (item.likes?.length - showLikes.length) : 0;
  const userLiked = item.likes?.find(like => like.user?._id === user._id);

  const isReserved = item.isReserved;
  const userReserved = isReserved && item.reservedBy === user._id;

  const handleLike = (e, item, emoji) => {
    e.stopPropagation();
    if (emoji !== 'X') toggleLike(item, emoji);
    setShowEmojiList(false);
  }

  const updateCommentCount = count => {
    setCommentCount(count);
    //setShowComments(false);
  }

  const ClaimReleaseButton = () => {
    if (isOwner) return null;
    else {
      if (userReserved) {
        return (<ConfirmButton
          text={`If you release this item, it will become available to other guests. 
               Would you like to release this item?`}
          className='release'
          confirm='Yes'
          cancel='No'
          onCancel={() => { }}
          onConfirm={() => onReleaseItem(item)}>
          Release Item
        </ConfirmButton>)
      }
      else return (<ConfirmButton
        text={`This item will be marked as claimed by you. The recipient will not know 
             this item has been claimed. Other guests will not see this item after you 
             confirm. Do you want to claim this item?`}
        className='buy'
        confirm='Yes'
        cancel='No'
        onCancel={() => { }}
        onConfirm={() => onClaimItem(item)}>
        Claim Item
      </ConfirmButton>)
    }
  }

  const gotoItem = () => {
    if (isOwner || userReserved) {
      navigateToItem();
    } else {
      setShowConfirmReserve(true);
    }
  }
  const confirmReserve = async () => {
    await onClaimItem(item);
    navigateToItem();
  }

  const navigateToItem = () => {
    setShowConfirmReserve(false);
    window.open(item.url, '_blank');
  }

  return (
    <div className={className}>
      {
        item.source === 'amazon' &&
        <div className='logo'>
          <img src={amazonLogo} alt='amazon logo' />
        </div>
      }
      <div className='data'>
        <div className='image'>
          <img src={item.image ? item.image : unknownImage} alt={item.description} />
        </div>
        <div className='details'>
          <div className='itemTitleBox'>
            <div className={`itemTitle ${item.url ? 'link': ''} `} onClick={item.url ? gotoItem : NO_OP}>{item.description.split(',').join(', ')}</div>
            <div className='by-line'>{item.by}</div>
          </div>
          {children}
          <div className='price'>{item.price}</div>
        </div>
      </div>
      <div className='controls'>
        <div className='likes' onClick={() => setShowEmojiList(true)}>
          {
            showEmojiList &&
            <div className='hover-likes'>
              {emojiList.map((emoji, i) => <span key={i} className={`emoji ${userLiked?.emoji === emoji ? 'selected' : ''}`} onClick={(e) => handleLike(e, item, emoji)}>{emoji}</span>)}
            </div>
          }
          <span className='button'>
            {
              userLiked
                ? <span className={userLiked ? 'user-liked' : ''}>{userLiked?.emoji}</span>
                : <FontAwesomeIcon icon={faThumbsUp} />
            }
          </span>
          <div className='show-likes'>
            <span>{showLikes?.length ?? 0}</span>
            {
              showLikes?.map((like, i) =>
                <span key={i} role="img" aria-label={'like'}>
                  {like.emoji}
                </span>)
            }
            {moreCount > 0 && <span>{`and ${moreCount} more`}</span>}
          </div>
        </div>

        <div className='comments' onClick={() => setShowComments(!showComments)}>
          <span className='icon'><FontAwesomeIcon icon={faMessage} /></span>
          <span>{commentCount ?? 0}</span>
        </div>
        <div className='buttons'>
          <ClaimReleaseButton />
          {
            canEditItem && (item.source !== 'amazon') &&
            <button onClick={() => onEditItem(item)}>
              <FontAwesomeIcon icon={faEdit} />
            </button>
          }
          {
            canDeleteItem &&
            <ConfirmButton
              text='Do you want to delete this item?'
              confirm='Yes'
              cancel='No'
              className='danger'
              onConfirm={() => onDeleteItem(item)}
              onCancel={() => { }}>
              <FontAwesomeIcon icon={faTrashCan} />
            </ConfirmButton>
          }
        </div>
      </div>
      {showComments && <Comments parent={item._id} users={members} parentModel='item' context={context} onUpdate={updateCommentCount} />}
      <Modal isOpen={showConfirmReserve}>
        <title>
          Would you like to claim this item?
        </title>
        <main>
          If you claim this item now, other guests will not see it on the list.
          If you decide you don't want to buy it, you can come back here and "release" it
          so that others can see it.
        </main>
        <footer>
          <button className='primary' onClick={navigateToItem}>No, just looking</button>
          <button className='primary' onClick={confirmReserve}>Yes, please claim it</button>
        </footer>
      </Modal>
    </div >
  )
})`${commonCss}`;

export default CommonItemInfo;